import { SkyBadge, SkyPopover, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { dbtNodeName, type DbtCause, type DbtCauseView } from "@shared/types/dbtCauses";

/**
 * A classified dbt failure cause (S7, canvas 18, contract §3.5; SEC-17).
 *
 * The label and the sentence are the SERVER's (`toDbtCauseView`); this component never composes
 * a sentence and never shows a code without its sentence. The chips are the identifiers the
 * image kept (a model, a column, a warehouse code, a count), each under a fixed label. dbt's and
 * the warehouse's own text is not here because it never left the container, and the "Why no
 * error text?" popover says so in words.
 *
 * `compact` (a table cell, a popover) drops the identifier chips but keeps the count.
 */

export interface CauseBlockProps {
  cause: DbtCauseView;
  compact?: boolean;
  testId?: string;
  /**
   * Round 6 (W7): "Why no error text?" only when the sentence carries no detail
   * (`causeHasErrorText`). Every surface that draws a cause passes it (#1374 item 3: a run's page
   * showed it beside rows with error text and beside skipped models; the graph, history and
   * tests panel had the same rule to follow).
   */
  whyOnlyWhenGeneric?: boolean;
}

/** The fixed copy of the "Why no error text?" popover (contract §3.5). */
export const WHY_NO_ERROR_TEXT =
  "skyData classifies the failure inside the dbt container and keeps only a code and the names involved. dbt's and the warehouse's own text can quote values from your data, so it never leaves the container.";

type Identifiers = DbtCauseView["identifiers"];

/**
 * What a `failures` count counts, which depends on the test (canvas 18 finding 4); one is
 * singular (round 6, W7: "1 values outside the list").
 */
export function failuresLabel(code: DbtCauseView["code"], count?: number): string {
  const one = count === 1;
  if (code === "TEST_ACCEPTED_VALUES_FAILED") {
    return one ? "value outside the list" : "values outside the list";
  }
  if (code === "TEST_UNIQUE_FAILED") return one ? "duplicated value" : "duplicated values";
  return one ? "failing row" : "failing rows";
}

/**
 * Round 6 (W7): whether the server's sentence carries the detail a person would call error text
 * (a relation, a column, a count). The generic ones say only that something failed, and only
 * there does "Why no error text?" answer a question the screen raises.
 */
function causeHasErrorText(cause: DbtCauseView): boolean {
  if (!cause.sentence.trim()) return false;
  if (cause.code === "UNCLASSIFIED" || cause.code === "DBT_RUNTIME_ERROR") return false;
  // #1674 review: a failed checkout with a known reason says what happened ("The dbt branch was
  // not found in the repository."), so it is not a generic phase sentence.
  if (
    cause.code === "PHASE_CLONE" &&
    SPECIFIC_CLONE_REASONS.has(String(cause.identifiers.cloneReason))
  ) {
    return true;
  }
  if (cause.code.startsWith("PHASE_")) return false;
  if (cause.code === "WAREHOUSE_ERROR" && cause.identifiers.warehouseCode === undefined) {
    return false;
  }
  return true;
}

/** #1674: the clone reasons whose sentence names the cause (`unknown` keeps the phase's words). */
const SPECIFIC_CLONE_REASONS: ReadonlySet<string> = new Set([
  "ref_not_found",
  "repo_unreadable",
  "auth",
]);

/** Round 6 (W3): a compact sentence longer than this folds to two lines with "Show more". */
const COMPACT_SENTENCE_FOLD = 80;

/** The codes and test kinds whose `failures` count values, not rows (canvas 18 finding 4). */
const VALUE_COUNT_CODES: ReadonlySet<string> = new Set([
  "TEST_ACCEPTED_VALUES_FAILED",
  "TEST_UNIQUE_FAILED",
]);
const VALUE_COUNT_KINDS: ReadonlySet<string> = new Set(["accepted_values", "unique"]);

/**
 * The short unit of a test's `failures` count in a table cell, so the cell agrees with the cause
 * chip on the same row: "values" for accepted_values and unique tests, "rows" otherwise. The
 * cause's code decides when there is one, else the test's kind; a test with neither reads "rows".
 * Given the count, one reads "row" or "value", never "1 rows".
 */
export function failuresUnit(
  test: {
    cause?: Pick<DbtCauseView, "code">;
    kind?: string;
  },
  count?: number,
): "values" | "rows" | "value" | "row" {
  const values = test.cause
    ? VALUE_COUNT_CODES.has(test.cause.code)
    : test.kind !== undefined && VALUE_COUNT_KINDS.has(test.kind);
  if (count === 1) return values ? "value" : "row";
  return values ? "values" : "rows";
}

function nameOf(uniqueId: string | undefined): string | undefined {
  return uniqueId ? dbtNodeName(uniqueId) : undefined;
}

/** The chips, in a fixed order, each with its fixed label. Never a value from the data. */
function chipsFor(
  code: DbtCauseView["code"],
  ids: Identifiers,
): { label: string; value: string }[] {
  const missing = code === "SOURCE_NOT_FOUND" ? "missing source" : "missing ref";
  const entries: [string, string | number | undefined][] = [
    ["model", nameOf(ids.model) ?? (code.startsWith("TEST_") ? undefined : nameOf(ids.node))],
    ["column", ids.column],
    ["relation", ids.relation],
    ["object kind", ids.objectKind],
    ["target type", ids.targetType],
    ["warehouse code", ids.warehouseCode],
    ["SQLSTATE", ids.sqlState],
    ["upstream", nameOf(ids.upstream)],
    [missing, ids.target],
    ["test kind", ids.testKind],
    ["line", ids.line],
    ["position", ids.position],
  ];
  return entries
    .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
    .map(([label, value]) => ({ label, value: String(value) }));
}

function nameWhyDialog(element: HTMLElement | null): void {
  element?.setAttribute("aria-label", "Why no error text?");
}

/**
 * A warn-severity test, or a node skipped because its upstream failed, reads as a warning: the
 * graph colours a skipped node `warning` (`nodeStatusPresentation`), and the label matches it.
 */
function isWarning(code: DbtCauseView["code"], identifiers: Pick<DbtCause, "severity">): boolean {
  return identifiers.severity === "warn" || code === "UPSTREAM_FAILED";
}

export function CauseBlock({
  cause,
  compact = false,
  testId = "cause-block",
  whyOnlyWhenGeneric = false,
}: CauseBlockProps) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [sentenceOpen, setSentenceOpen] = useState(false);
  const chips = compact ? [] : chipsFor(cause.code, cause.identifiers);
  const failures = cause.identifiers.failures;
  const tone = isWarning(cause.code, cause.identifiers) ? "warning" : "danger";
  // Round 6 (W3): in a table cell a relation name has no break point and ran into the next
  // column; it wraps anywhere, and a long sentence folds to two lines with an expander.
  const folds = compact && cause.sentence.length > COMPACT_SENTENCE_FOLD;
  // A selector the runner refused before dbt started has its own sentence, and no dbt output to miss.
  const showWhy =
    cause.code !== "SELECTOR_NOT_VALID" && (!whyOnlyWhenGeneric || !causeHasErrorText(cause));
  const clamp = folds && !sentenceOpen ? " line-clamp-2" : "";
  return (
    <div
      className="flex min-w-0 flex-col gap-2"
      data-testid={testId}
      data-cause-code={cause.code}
      data-cause-tone={tone}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SkyBadge appearance="tint" color={tone} testId={`${testId}-label`}>
          {cause.label}
        </SkyBadge>
        {failures !== undefined ? (
          <SkyBadge appearance="outline" color="subtle" testId={`${testId}-failures`}>
            {`${failures} ${failuresLabel(cause.code, failures)}`}
          </SkyBadge>
        ) : null}
      </div>
      <SkyText
        size={compact ? 200 : 300}
        testId={`${testId}-sentence`}
        className={compact ? `min-w-0 wrap-anywhere${clamp}` : undefined}
        block={compact}
      >
        {cause.sentence}
      </SkyText>
      {folds ? (
        <button
          type="button"
          className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={sentenceOpen}
          onClick={() => setSentenceOpen((open) => !open)}
          data-testid={`${testId}-more`}
        >
          {sentenceOpen ? "Show less" : "Show more"}
        </button>
      ) : null}
      {chips.length > 0 ? (
        <ul className="flex flex-wrap gap-1" aria-label="What the cause names">
          {chips.map((chip) => (
            // A long relation must not widen the page at phone width (390 px): the chip is capped
            // at its row and truncates with an ellipsis; the full value is in the item's title.
            <li
              key={chip.label}
              className="min-w-0 max-w-full"
              title={`${chip.label} ${chip.value}`}
            >
              <SkyBadge appearance="outline" color="subtle" maxWidth="100%">
                <span className="text-muted-foreground">{chip.label}</span>{" "}
                <span className="font-mono">{chip.value}</span>
              </SkyBadge>
            </li>
          ))}
        </ul>
      ) : null}
      {showWhy ? (
        <SkyPopover
          // SkyPopover renders a role=dialog and forwards no aria props, so the dialog is named
          // through its ref: an unnamed dialog is an axe violation (NFR-7).
          ref={nameWhyDialog}
          open={whyOpen}
          onOpenChange={setWhyOpen}
          placement="bottom-start"
          className="max-w-xs p-3"
          testId={`${testId}-why`}
          trigger={
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={whyOpen}
              onClick={() => setWhyOpen((open) => !open)}
              data-testid={`${testId}-why-button`}
            >
              Why no error text?
            </button>
          }
        >
          <SkyText size={200}>{WHY_NO_ERROR_TEXT}</SkyText>
        </SkyPopover>
      ) : null}
    </div>
  );
}

/** The muted line a failed node or test shows when the image classified nothing. */
export function CauseNotClassified({ testId = "cause-not-classified" }: { testId?: string }) {
  return (
    <SkyText size={200} color="muted" testId={testId}>
      Cause not classified
    </SkyText>
  );
}
